import { getCanonicalUrl, normalizeCanonicalPath } from "./seo";
import spotPagesJson from "./spotPages.json";

export interface SpotPageConfig {
  path: string;
  regionId: string;
  cameraId: string;
  spotName: string;
  displayName: string;
  title: string;
  description: string;
  introTitle: string;
  introBody: string;
  city: string;
  state: string;
  lat: number;
  lon: number;
  relatedPaths: string[];
  popularitySource?: "manual_seed" | "analytics_seed" | "search_console_opportunity";
  /** Directory rank. Higher pages sort first on hub and map surfaces. */
  priorityScore?: number;
  /** Sitemap `<priority>` override. The prerender script defaults this to 0.9. */
  priority?: string;
}

export interface SpotPageStructuredData {
  "@context": "https://schema.org";
  "@type": "WebPage";
  name: string;
  description: string;
  url: string;
  isPartOf: {
    "@type": "WebSite";
    name: "howisthe.surf";
    url: string;
  };
  about: {
    "@type": "Place";
    name: string;
    address: {
      "@type": "PostalAddress";
      addressLocality: string;
      addressRegion: string;
      addressCountry: string;
    };
    geo: {
      "@type": "GeoCoordinates";
      latitude: number;
      longitude: number;
    };
  };
}

export const spotPages = spotPagesJson as SpotPageConfig[];

export function getSpotPageByPath(pathname: string): SpotPageConfig | null {
  const normalizedPath = normalizeCanonicalPath(pathname);

  return (
    spotPages.find((page) => normalizeCanonicalPath(page.path) === normalizedPath) ?? null
  );
}

export function getSpotPageByRegionBreak(
  regionId: string,
  breakId: string,
): SpotPageConfig | null {
  return (
    spotPages.find((page) => page.regionId === regionId && page.cameraId === breakId) ??
    null
  );
}

export function getSpotPageCountryCode(page: SpotPageConfig): string {
  if (page.regionId.startsWith("Praias ")) return "PT";
  if (page.state === "FL") return "US";
  return page.state;
}

export function getRelatedSpotPages(page: SpotPageConfig): SpotPageConfig[] {
  return page.relatedPaths
    .map((path) => getSpotPageByPath(path))
    .filter((relatedPage): relatedPage is SpotPageConfig => Boolean(relatedPage));
}

export function buildSpotPageStructuredData(
  page: SpotPageConfig,
): SpotPageStructuredData {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    description: page.description,
    url: getCanonicalUrl(page.path),
    isPartOf: {
      "@type": "WebSite",
      name: "howisthe.surf",
      url: getCanonicalUrl("/"),
    },
    about: {
      "@type": "Place",
      name: page.displayName,
      address: {
        "@type": "PostalAddress",
        addressLocality: page.city,
        addressRegion: page.state,
        addressCountry: getSpotPageCountryCode(page),
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: page.lat,
        longitude: page.lon,
      },
    },
  };
}
